import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const TOPIC_LABELS = {
    certified_course: "Certified Course",
    rope_belt_therapy_yoga: "Rope & Belt Therapy Yoga",
    advance_asana: "Advance Asana",
    yoga_meditation: "Yoga & Meditation",
    health_workshop: "Health Workshop",
    corporate_workshop: "Corporate Workshop",
};

const MODE_LABELS = {
    online: "Online",
    offline: "On Campus / Studio",
    home_tutions: "Home Tuitions",
};

const STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "in_progress", label: "In progress" },
    { value: "responded", label: "Responded" },
    { value: "closed", label: "Closed" },
];

const STATUS_TONE = {
    new: "bg-accent-50 text-accent-700",
    in_progress: "bg-amber-50 text-amber-700",
    responded: "bg-brand-50 text-brand-700",
    closed: "bg-gray-100 text-ink-700",
};

export default function EnquiriesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [expanded, setExpanded] = useState(null);

    const reload = () => {
        const url = filter === "all" ? "/admin/enquiries" : `/admin/enquiries?status=${filter}`;
        return api.get(url).then(({ data }) => setItems(data)).catch(() => setItems([]));
    };

    useEffect(() => { setLoading(true); reload().finally(() => setLoading(false)); }, [filter]); // eslint-disable-line

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}?status=${status}`);
            toast.success("Status updated");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this enquiry?")) return;
        try {
            await api.delete(`/admin/enquiries/${id}`);
            toast.success("Enquiry deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid="enquiries-tab">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-semibold">Enquiries</h2>
                    <p className="text-sm text-ink-700 mt-0.5">Submissions from the "Write to Us" form on the homepage.</p>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-40 rounded-full" data-testid="enquiries-filter">
                        <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="text-center py-10 text-ink-700">Loading…</div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-ink-700">
                    <Mail className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                    No enquiries to show.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((e) => (
                        <div key={e.id} className="rounded-2xl bg-white border border-gray-100 shadow-soft p-4" data-testid={`enquiry-row-${e.id}`}>
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-heading font-semibold text-ink-900">{e.first_name} {e.last_name}</div>
                                        <a href={`mailto:${e.email}`} className="text-xs text-brand-700 hover:underline">{e.email}</a>
                                        <Badge className={`${STATUS_TONE[e.status] || ""} rounded-full text-[10px] uppercase font-semibold`}>{e.status.replace("_", " ")}</Badge>
                                    </div>
                                    <div className="text-[11px] text-ink-400 mt-0.5">Received {new Date(e.created_at).toLocaleString()}</div>
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {(e.topics || []).map((t) => (
                                            <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">{TOPIC_LABELS[t] || t}</span>
                                        ))}
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent-50 text-accent-700">{MODE_LABELS[e.mode] || e.mode}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                                        <SelectTrigger className="w-36 rounded-full h-8 text-xs" data-testid={`enquiry-status-${e.id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8" onClick={() => setExpanded((x) => x === e.id ? null : e.id)} data-testid={`enquiry-toggle-${e.id}`}>
                                        {expanded === e.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                    </Button>
                                    <Button size="icon" variant="outline" className="rounded-full h-8 w-8 text-accent-700 hover:bg-accent-50" onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                            {expanded === e.id && (
                                <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-ink-900 whitespace-pre-line" data-testid={`enquiry-message-${e.id}`}>
                                    {e.message}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
